import React, { ReactNode, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Card,
  Image,
  Modal,
} from "react-bootstrap";
import { FaCheckCircle, FaSearch, FaTimesCircle } from "react-icons/fa";
import { ENV } from "../../../config/env";
import axios from "axios";
import { DEFAULT_WEB_TITLE } from "../../../constants";
import { PRODUCTS } from "../../../constants/products";

const USER_TYPE = {
  poFinancing: "PO_FINANCING",
  invoiceFinancing: "INVOICE_FINANCING",
  capitalLoan: "WORKING_CAPITAL",
  employeeLoan: "EMPLOYEE",
};

export type ValidUserType = keyof typeof USER_TYPE;
type UserTypeValue = (typeof USER_TYPE)[ValidUserType];

interface IProps {
  withProductFilter?: boolean;
  foundMessage?: string;
  notFoundMessage?: string;
  subtitle?: string | ReactNode;
  filters: ValidUserType[];
}
interface Payload {
  search_query?: string;
  user_type?: UserTypeValue;
}

function isValidUserType(type: string): type is ValidUserType {
  return type in USER_TYPE;
}

const MIN_CHARACTER = 3;

function CheckCompany({
  withProductFilter = false,
  foundMessage = "",
  notFoundMessage = "",
  subtitle = "",
  filters = [],
}: IProps) {
  const [oldFilters, setOldFilters] = useState(filters);
  const [searchQueryContent, setSearchQueryContent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [searchContent, setSearchContent] = useState([] as any[]);
  const [searchResult, setSearchResult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const submitForm = async () => {
    try {
      setSearchMessage("");
      setSearchContent([]);
      setIsSearch(false);
      if (searchQuery === "") {
        return;
      }
      if (searchQuery.length < MIN_CHARACTER) {
        setIsSearch(true);
        setSearchMessage(
          `Harap masukkan minimal ${MIN_CHARACTER} karakter untuk mulai pencarian.`
        );
        return;
      }
      setIsLoading(true);
      let payload: Payload = {
        search_query: searchQuery,
      };
      if (
        (filters || []).length > 0 &&
        (filters || []).length < Object.keys(PRODUCTS).length
      ) {
        let found: string[] = [];
        let products: string[] = [];
        let contents: any[] = [];
        for (let userType of filters) {
          if (!userType || !isValidUserType(userType)) {
            continue;
          }
          payload.user_type = USER_TYPE[userType];
          products.push(PRODUCTS[userType].title);
          let response = await axios.post(
            `${ENV.API_URL}/pohondana/partner/check-partner`,
            payload
          );
          if (response.data.code === 200) {
            const content = (response.data.content || []).filter(
              (c: any) => c.company_name || c.brand
            );
            if (content.length > 0) {
              found.push(PRODUCTS[userType].title);
              contents = [...contents, ...content];
            }
          }
        }
        if (found.length > 0) {
          let text = "";
          if (found.length === 1) {
            text = found.join(", ");
          } else if (found.length === 2) {
            text = found.join(" dan ");
          } else {
            found[found.length - 1] = " dan " + found[found.length - 1];
            text = found.join(", ");
          }
          setSearchContent(
            contents.map((c, cidx) => {
              return {
                ...c,
                key: cidx,
              };
            })
          );
          setSearchMessage(
            `Ada ${new Intl.NumberFormat(["ban", "id"]).format(contents.length)} perusahaan dengan nama / brand "${searchQuery}" yang memiliki produk ${text} aktif di Pohon Dana. ${foundMessage}`
          );
          setSearchResult(true);
        } else {
          let text = "";
          if (products.length === 1) {
            text = products.join(", ");
          } else if (products.length === 2) {
            text = products.join(" dan ");
          } else {
            products[products.length - 1] =
              " dan " + products[products.length - 1];
            text = products.join(", ");
          }
          setSearchMessage(
            `Perusahaan ini tidak memiliki produk ${text} aktif di Pohon Dana. ${notFoundMessage}`
          );
          setSearchResult(false);
        }
        setIsSearch(true);
      } else {
        let response = await axios.post(
          `${ENV.API_URL}/pohondana/partner/check-partner`,
          payload
        );
        if (response.data.code === 200) {
          const content = (response.data.content || []).filter(
            (c: any) => c.company_name || c.brand
          );
          if (content.length > 0) {
            setSearchContent(
              content.map((c: any, cidx: any) => {
                return {
                  ...c,
                  key: cidx,
                };
              })
            );
            setSearchMessage(
              `Ada ${new Intl.NumberFormat(["ban", "id"]).format(content.length)} perusahaan dengan nama / brand "${searchQuery}" yang telah bergabung pada ekosistem Pohon Dana. ${foundMessage}`
            );
            setSearchResult(true);
          } else {
            setSearchMessage(
              `Perusahaan ini tidak ditemukan pada ekosistem Pohon Dana. ${notFoundMessage}`
            );
            setSearchResult(false);
          }
          setIsSearch(true);
        }
      }
      setIsLoading(false);
    } catch (exception) {
      console.log(exception);
    }
  };

  useEffect(() => {
    if (JSON.stringify(oldFilters) !== JSON.stringify(filters)) {
      submitForm();
    }
    setOldFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <React.Fragment>
      {showSearchResult && (
        <Modal
          scrollable
          show
          size="lg"
          onHide={() => setShowSearchResult(false)}
        >
          <Modal.Header closeButton>
            <div
              style={{
                minWidth: 300,
                width: "80%",
              }}
            >
              {new Intl.NumberFormat(["ban", "id"]).format(
                searchContent.length
              )}{" "}
              hasil pencarian atas
              <span className="text-primary-main mx-2">"{searchQuery}"</span>
              {filters.length > 0
                ? ` untuk produk ${filters.map((u) => PRODUCTS[u].title).join(", ")}`
                : null}
              <div className="mt-2 w-100">
                <Form>
                  <Form.Control
                    className="no-bg w-100"
                    type="text"
                    value={searchQueryContent}
                    onChange={(e) => {
                      setSearchQueryContent(e.target.value);
                    }}
                    placeholder="Cari dalam daftar"
                  />
                </Form>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <Row>
              {searchContent
                .filter((c) => {
                  if (searchQueryContent.length === 0) {
                    return true;
                  }
                  return (
                    (c.company_name || "")
                      .toLowerCase()
                      .includes(searchQueryContent.toLowerCase()) ||
                    (c.brand || "")
                      .toLowerCase()
                      .includes(searchQueryContent.toLowerCase())
                  );
                })
                .map((c) => {
                  return (
                    <Col key={c.key} sm={12} md={6} lg={6}>
                      <div className="p-2 border-bottom h-100">
                        {c.company_name ? (
                          <React.Fragment>
                            <div className="text-size-caption text-primary">
                              {c.company_name}
                            </div>
                            {c.brand ? (
                              <div className="text-size-label text-secondary">
                                ( {c.brand} )
                              </div>
                            ) : null}
                          </React.Fragment>
                        ) : c.brand ? (
                          <div className="text-size-caption text-primary">
                            {c.brand}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  );
                })}
            </Row>
          </Modal.Body>
        </Modal>
      )}
      <Card className={`h-100 border-0`}>
        <Card.Body className="py-2 py-lg-4 px-4 px-lg-5">
          <Row className="my-2 py-2">
            <Col
              xl={withProductFilter ? "auto" : 6}
              lg={withProductFilter ? 12 : 6}
              md={12}
              sm={12}
            >
              <div className="text-size-body-head">
                Cek perusahaan yang telah bergabung di
              </div>
              <div>
                <Image
                  src={`${ENV.URL}/images/logo-black.png`}
                  alt=""
                  title={DEFAULT_WEB_TITLE}
                  width={180}
                />
              </div>
              {subtitle && (
                <div className="mt-2 text-size-caption text-secondary-dark">
                  {typeof subtitle === "string" ? (
                    <span className="text-primary-main fw-bold text-size-sub-body-2">
                      *
                    </span>
                  ) : null}{" "}
                  {subtitle}
                </div>
              )}
            </Col>
            <Col
              xl
              lg={withProductFilter ? 12 : 6}
              md={12}
              sm={12}
              className="mt-2 mt-lg-0"
            >
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitForm();
                }}
              >
                <div className="text-size-caption mb-2">
                  Masukkan brand, nama, atau NPWP perusahaan (minimal 3
                  karakter)
                </div>
                <div className="form-search">
                  <div className="d-flex justify-content-center align-items-center p-2">
                    <FaSearch size={20} />
                  </div>
                  <div
                    style={{ width: "100%" }}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <Form.Control
                      className="form-input-search"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsSearch(false);
                        setSearchResult(false);
                        setSearchMessage("");
                      }}
                      placeholder="brand, nama, atau NPWP perusahaan"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          submitForm();
                        }
                      }}
                    />
                  </div>
                  <div
                    onClick={() => {
                      setSearchQuery("");
                      setIsSearch(false);
                    }}
                    className="d-flex justify-content-center align-items-center p-2"
                  >
                    <FaTimesCircle
                      style={{
                        cursor: "pointer",
                      }}
                      size={20}
                    />
                  </div>
                  <div className="d-flex justify-content-center align-items-center p-2">
                    <Button
                      disabled={isLoading}
                      onClick={() => {
                        submitForm();
                      }}
                    >
                      {isLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Cari"
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
              {isSearch && (
                <React.Fragment>
                  <div
                    className={`${
                      searchResult ? "text-primary-main" : " text-error-main"
                    } mt-2 d-flex align-items-center gap-2 `}
                  >
                    {searchResult ? (
                      <FaCheckCircle size={22} />
                    ) : (
                      <FaTimesCircle size={22} />
                    )}

                    <span className="text-size-caption">{searchMessage}</span>
                  </div>
                  {searchContent.length > 0 && (
                    <div className="text-center mt-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowSearchResult(true)}
                      >
                        Lihat semua hasil pencarian
                      </Button>
                    </div>
                  )}
                </React.Fragment>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}

export default CheckCompany;
